import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { first } from 'rxjs';

import { YshPageHeaderComponent } from '../../components/ysh-page-header/ysh-page-header.component';
import { YshPanelComponent } from '../../components/ysh-panel/ysh-panel.component';
import { YshButtonComponent } from '../../components/ysh-button/ysh-button.component';
import { YshBadgeComponent } from '../../components/ysh-badge/ysh-badge.component';
import {
  ComponentWithInputs,
  TableDataSouce,
  TableFilter,
  YshTableSetComponent,
} from '../../components/ysh-table-set/ysh-table-set.component';

import { CompanyService } from '../../services/api/company/company.service';
import { Action, PermissionService, Resource } from '../../services/permission/permission.service';
import { ModalService } from '../../services/ui/modal/modal.service';
import { ToastService } from '../../services/ui/toast/toast.service';
import { DialogService } from '../../services/ui/dialog/dialog.service';
import { IntegrationService } from '../../services/api/integration/integration.service';

import { EventType, Integration, RotateSecretParams, TestResult } from '../../models/integration';
import { DeliveryStatus, WebhookDelivery } from '../../models/webhook-delivery';
import { WebhookDeliveryDetailComponent } from './webhook-delivery-detail/webhook-delivery-detail.component';

const ALL_EVENT_TYPES: EventType[] = [
  'inspection.completed',
  'appointment.created',
  'appointment.updated',
  'appointment.canceled',
];
const DELIVERY_STATUSES: DeliveryStatus[] = ['pending', 'processing', 'succeeded', 'failed', 'dead'];

@Component({
    selector: 'app-webhooks',
    templateUrl: './webhooks.component.html',
    styleUrl: './webhooks.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    FormsModule,
    RouterModule,
    MatIconModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    YshPageHeaderComponent,
    YshPanelComponent,
    YshButtonComponent,
    YshTableSetComponent
]
})
export class WebhooksComponent implements OnInit {
  @Input() companyUid!: string;

  readonly allEventTypes = ALL_EVENT_TYPES;
  readonly deliveryStatuses = DELIVERY_STATUSES;

  readonly docsRoute = '/docs/webhooks/overview';

  // Access / load state
  authorized = false;
  loading = true;
  notProvisioned = false;
  integration?: Integration;

  // Editable form state
  webhookUrl = '';
  enabled = false;
  selectedEventTypes = new Set<EventType>();

  // Action state
  saving = false;
  testing = false;
  rotating = false;

  // Results
  revealedSecret?: string;
  rotatedPreviousSecretUntil?: string;
  testResult?: TestResult;

  // Deliveries
  deliveriesDataSource?: TableDataSouce<WebhookDelivery>;

  constructor(
    private integrationService: IntegrationService,
    private companyService: CompanyService,
    private permissionService: PermissionService,
    private modalService: ModalService,
    private toast: ToastService,
    private dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.companyService.currentCompany$.pipe(first((company) => !!company)).subscribe((company) => {
      this.permissionService.getPermissions(company, [Resource.PartnerIntegration]).subscribe((permissions) => {
        this.authorized = permissions.canAccess(Action.Get, Resource.PartnerIntegration);
        if (this.authorized) {
          this.loadIntegration();
        } else {
          this.loading = false;
        }
      });
    });
  }

  private loadIntegration(): void {
    this.loading = true;
    this.integrationService.get(this.companyUid).subscribe({
      next: (integration) => {
        this.applyIntegration(integration);
        this.buildDeliveriesDataSource();
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        if (err.status === 404) {
          this.notProvisioned = true;
        } else {
          this.toast.show('error', 'Unable to load the webhook integration.');
        }
      },
    });
  }

  private applyIntegration(integration: Integration): void {
    this.integration = integration;
    this.webhookUrl = integration.webhookUrl || '';
    this.enabled = integration.enabled;
    this.selectedEventTypes = new Set(integration.eventTypes || []);
  }

  // Settings actions

  toggleEventType(eventType: EventType, checked: boolean): void {
    if (checked) {
      this.selectedEventTypes.add(eventType);
    } else {
      this.selectedEventTypes.delete(eventType);
    }
  }

  isEventTypeSelected(eventType: EventType): boolean {
    return this.selectedEventTypes.has(eventType);
  }

  get hasUnsavedChanges(): boolean {
    if (!this.integration) {
      return false;
    }
    const sameEvents =
      this.selectedEventTypes.size === (this.integration.eventTypes?.length || 0) &&
      (this.integration.eventTypes || []).every((e) => this.selectedEventTypes.has(e));
    return (
      this.webhookUrl !== (this.integration.webhookUrl || '') ||
      this.enabled !== this.integration.enabled ||
      !sameEvents
    );
  }

  handleSave(): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.integrationService
      .update(this.companyUid, {
        webhookUrl: this.webhookUrl,
        enabled: this.enabled,
        eventTypes: Array.from(this.selectedEventTypes),
      })
      .subscribe({
        next: (integration) => {
          this.applyIntegration(integration);
          this.saving = false;
          this.toast.show('success', 'Webhook settings saved.');
        },
        error: (err: HttpErrorResponse) => {
          this.saving = false;
          this.toast.show('error', this.errorMessage(err, 'Unable to save settings.'));
        },
      });
  }

  /** Kill switch — persists the enabled flag immediately. */
  handleToggleEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.integrationService.update(this.companyUid, { enabled }).subscribe({
      next: (integration) => {
        this.applyIntegration(integration);
        this.toast.show('success', enabled ? 'Webhook enabled.' : 'Webhook disabled.');
      },
      error: (err: HttpErrorResponse) => {
        this.enabled = !enabled; // revert
        this.toast.show('error', this.errorMessage(err, 'Unable to update the kill switch.'));
      },
    });
  }

  handleSendTest(): void {
    if (this.testing) {
      return;
    }
    this.testing = true;
    this.testResult = undefined;
    this.integrationService.test(this.companyUid).subscribe({
      next: (result) => {
        this.testResult = result;
        this.testing = false;
      },
      error: (err: HttpErrorResponse) => {
        this.testing = false;
        this.toast.show('error', this.errorMessage(err, 'Test request failed.'));
      },
    });
  }

  handleRotateSecret(): void {
    // If a previously rotated secret is still in its grace window, the API
    // rejects a plain rotation — only a forced rotation (which invalidates the
    // old secret immediately) is allowed.
    if (this.integration?.hasPreviousSecretActive) {
      this.confirmForceRotate();
    } else {
      this.dialogService.showDialog({
        heading: 'Rotate signing secret?',
        caption:
          'A new signing secret will be generated and shown once. Your previous secret stays valid for a 24-hour grace period so you can update your endpoint without missing deliveries.',
        buttonLabelCancel: 'Cancel',
        buttonLabelConfirm: 'Rotate Secret',
        buttonThemeConfirm: 'coral',
        onConfirm: () => this.rotateSecret({ graceHours: 24 }),
      });
    }
  }

  private confirmForceRotate(): void {
    const until = this.integration?.previousSecretActiveUntilFormatted;
    this.dialogService.showDialog({
      heading: 'Force rotate signing secret?',
      caption:
        `A previously rotated secret is still active${until ? ` until ${until}` : ''}. ` +
        'Rotating again will invalidate it immediately, so <strong>any endpoint still verifying with the old secret will start failing</strong>. Continue?',
      buttonLabelCancel: 'Cancel',
      buttonLabelConfirm: 'Force Rotate',
      buttonThemeConfirm: 'coral',
      onConfirm: () => this.rotateSecret({ graceHours: 24, force: true }),
    });
  }

  private rotateSecret(params: RotateSecretParams): void {
    this.rotating = true;
    this.revealedSecret = undefined;
    this.integrationService.rotateSecret(this.companyUid, params).subscribe({
      next: (integration) => {
        this.applyIntegration(integration);
        this.revealedSecret = integration.signingSecret;
        this.rotatedPreviousSecretUntil = integration.hasPreviousSecretActive
          ? integration.previousSecretActiveUntilFormatted
          : undefined;
        this.rotating = false;
        if (!this.revealedSecret) {
          this.toast.show('warning', 'Secret rotated, but no value was returned.');
        }
      },
      error: (err: HttpErrorResponse) => {
        this.rotating = false;
        // The API refuses to rotate over an active previous secret unless forced.
        if (err.status === 400 && !params.force) {
          this.confirmForceRotate();
          return;
        }
        this.toast.show('error', this.errorMessage(err, 'Unable to rotate the secret.'));
      },
    });
  }

  copySecret(): void {
    if (!this.revealedSecret) {
      return;
    }
    navigator.clipboard?.writeText(this.revealedSecret).then(
      () => this.toast.show('success', 'Signing secret copied to clipboard.'),
      () => this.toast.show('error', 'Could not copy to clipboard.'),
    );
  }

  dismissRevealedSecret(): void {
    this.revealedSecret = undefined;
    this.rotatedPreviousSecretUntil = undefined;
  }

  // Deliveries

  private buildDeliveriesDataSource(): void {
    this.deliveriesDataSource = {
      title: '',
      columns: [
        { field: 'eventId', title: 'Event ID', cellSize: 'large' },
        { field: 'eventType', title: 'Event Type', cellSize: 'large' },
        {
          title: 'Status',
          componentWithInputs: (item) =>
            new ComponentWithInputs(YshBadgeComponent, {
              value: item.status,
              theme: item.statusBadgeTheme,
            }),
        },
        { field: 'responseStatus', title: 'Response', cellSize: 'small' },
        { field: 'createdAtFormatted', title: 'Created' },
      ],
      displayRowNav: true,
      filters: [
        {
          type: TableFilter.Select,
          key: 'status',
          label: 'Status',
          options: this.deliveryStatuses.map((status) => ({ label: status, value: status })),
        },
        {
          type: TableFilter.Select,
          key: 'eventType',
          label: 'Event Type',
          options: this.allEventTypes.map((eventType) => ({ label: eventType, value: eventType })),
        },
      ],
      actions: [],
      noDataMessage: 'No webhook deliveries yet.',
      load: (state) => this.integrationService.getDeliveries(this.companyUid, state),
    };
  }

  refreshDeliveries(): void {
    // Re-assigning the dataSource triggers a reload in ysh-table-set while
    // preserving its current filter/page state.
    this.buildDeliveriesDataSource();
  }

  handleOpenDelivery(delivery: WebhookDelivery): void {
    this.modalService.present({
      component: WebhookDeliveryDetailComponent,
      cssClass: 'webhook-delivery-modal',
      componentProps: {
        companyUid: this.companyUid,
        eventId: delivery.eventId,
        onResend: () => this.refreshDeliveries(),
      },
    });
  }

  private errorMessage(err: HttpErrorResponse, fallback: string): string {
    return err?.error?.message || err?.error?.errors?.[0]?.message || fallback;
  }
}
